import React, { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getReactionLabel } from '../utils/reactionLabels';

const SortableImageCard = ({ 
  image, 
  index, 
  isSelected, 
  onDelete, 
  onImageClick, 
  onFooterClick,
  getReactionIcon,
  reorderEnabled,
  sectionLabels,
  onUpdateLabel,
  onCreateLabel,
  onToggleFinal,
  onSaveOwnerReply,
  allowImageConversations = true,
  reactionMode = 'mood',
  brandName = '',
  clientName = '',
  sharedDecisionMode,
  participants,
  isCover,
  // When true, render the original uploaded filename below the image card
  // (owner-only — clients never see this). Default false keeps the editorial
  // view clean. Truncates with ellipsis; never used as a React key.
  showFilename = false,
  // Gallery Sections (Etap 5) — the list of available sections + this
  // image's current assignment + a callback to move it. Passed from
  // BoardImageGrid; safe defaults keep the component happy on surfaces
  // that don't use sections (Inspiration boards, legacy calls).
  gallerySections = [],
  currentSectionId = null,
  onMoveToSection = null,
}) => {
  const [showSectionMenu, setShowSectionMenu] = useState(false);
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [newLabelInput, setNewLabelInput] = useState('');
  // Owner reply state — local draft so we save on blur (mirrors the client
  // comment UX). Empty reply clears the field on the backend.
  const [ownerReply, setOwnerReply] = useState(image.owner_reply || '');
  const [showOwnerReply, setShowOwnerReply] = useState(!!image.owner_reply);
  useEffect(() => {
    setOwnerReply(image.owner_reply || '');
    setShowOwnerReply(prev => prev || !!image.owner_reply);
  }, [image.owner_reply]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : (showLabelDropdown ? 50 : 1),
  };

  const dragProps = reorderEnabled ? { ...attributes, ...listeners } : {};

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowLabelDropdown(false);
        setNewLabelInput('');
      }
    };
    if (showLabelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLabelDropdown]);

  const handleLabelClick = (e) => {
    e.stopPropagation();
    setShowLabelDropdown(!showLabelDropdown);
    setNewLabelInput('');
  };

  const handleSelectLabel = (label) => {
    onUpdateLabel(image.id, label);
    setShowLabelDropdown(false);
    setNewLabelInput('');
  };

  const handleCreateNewLabel = () => {
    const trimmed = newLabelInput.trim();
    if (!trimmed) return;
    const exists = sectionLabels.some(l => l.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      const existing = sectionLabels.find(l => l.toLowerCase() === trimmed.toLowerCase());
      handleSelectLabel(existing);
    } else {
      onCreateLabel(trimmed, image.id);
      setShowLabelDropdown(false);
      setNewLabelInput('');
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateNewLabel();
    } else if (e.key === 'Escape') {
      setShowLabelDropdown(false);
      setNewLabelInput('');
    }
  };

  return (
    <div 
      ref={setNodeRef}
      {...dragProps}
      style={{
        ...style,
        cursor: reorderEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        touchAction: reorderEnabled ? 'none' : 'auto',
        outline: reorderEnabled ? '2px dashed var(--kahla-border)' : 'none',
        outlineOffset: '-2px',
        position: 'relative',
        boxShadow: image.is_final ? '0 0 0 2px #C9A96E' : 'none',
      }}
      className="image-card"
      data-testid={`sortable-image-${image.id}`}
    >
      {/* Final badge */}
      {image.is_final && (
        <div
          data-testid={`final-badge-${image.id}`}
          style={{
            position: 'absolute',
            top: '8px',
            right: '38px',
            backgroundColor: 'rgba(201,169,110,0.92)',
            color: '#fff',
            fontSize: '9px',
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '3px 8px',
            zIndex: 12,
            pointerEvents: 'none',
          }}
        >
          Final
        </div>
      )}
      {/* Cover badge */}
      {isCover && (
        <div
          data-testid={`cover-badge-${image.id}`}
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            backgroundColor: 'rgba(58, 50, 42, 0.88)',
            color: '#fff',
            fontSize: '9px',
            fontWeight: '500',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '3px 9px',
            zIndex: 12,
            pointerEvents: 'none',
            backdropFilter: 'blur(6px)',
            borderRadius: '999px',
          }}
        >
          Cover
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '24px',
          height: '24px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 11,
          fontSize: '12px',
          color: 'var(--kahla-text-light)',
          opacity: 0,
          transition: 'opacity 0.2s',
          pointerEvents: 'none',
        }}
        className="drag-handle"
      >
        ⋮⋮
      </div>
      <button
        data-testid={`delete-image-${image.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(image.id);
        }}
        style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.6)',
          border: 'none',
          color: 'white',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.2s',
          zIndex: 10
        }}
        className="image-delete-btn"
        title="Remove from board"
      >
        ✕
      </button>

      {/* Move to section menu — visible only when the board has
          at least one Gallery Section defined. Small circular button
          sitting next to the delete affordance, appears on hover
          alongside the other card overlays. */}
      {onMoveToSection && gallerySections.length > 0 && (
        <div
          style={{ position: 'absolute', top: '8px', left: '44px', zIndex: 12 }}
          data-testid={`image-move-section-wrap-${image.id}`}
        >
          <button
            data-testid={`image-move-section-btn-${image.id}`}
            onClick={(e) => { e.stopPropagation(); setShowSectionMenu((v) => !v); }}
            className="image-move-section-btn"
            title="Move to section"
            style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.6)', border: 'none',
              color: 'white', fontSize: '14px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: 0, transition: 'opacity 0.2s',
            }}
          >
            ⇄
          </button>
          {showSectionMenu && (
            <div
              onClick={(e) => e.stopPropagation()}
              data-testid={`image-move-section-menu-${image.id}`}
              style={{
                position: 'absolute', top: '32px', left: 0,
                minWidth: '200px', maxHeight: '260px', overflowY: 'auto',
                background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)',
                boxShadow: '0 8px 24px rgba(42,38,32,0.12)',
                padding: '4px 0', fontSize: '13px', color: 'var(--kahla-text)',
              }}
            >
              <button
                type="button"
                onClick={() => { onMoveToSection(null); setShowSectionMenu(false); }}
                data-testid={`image-move-section-none-${image.id}`}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 14px', background: currentSectionId ? 'transparent' : 'var(--kahla-cream-light)',
                  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontStyle: 'italic', color: 'var(--kahla-text-light)',
                }}
              >
                — Uncategorized —
              </button>
              {gallerySections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onMoveToSection(s.id); setShowSectionMenu(false); }}
                  data-testid={`image-move-section-item-${image.id}-${s.id}`}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 14px',
                    background: currentSectionId === s.id ? 'var(--kahla-cream-light)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    color: 'var(--kahla-text)',
                  }}
                  onMouseEnter={(e) => { if (currentSectionId !== s.id) e.currentTarget.style.background = 'var(--kahla-cream-light, #f7f3ea)'; }}
                  onMouseLeave={(e) => { if (currentSectionId !== s.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  {s.title || '(untitled)'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div 
        className="image-card-image"
        data-testid={`image-clickable-${image.id}`}
        onClick={() => onImageClick(index)}
        style={{ 
          cursor: 'zoom-in',
          border: isSelected ? '2px solid var(--kahla-charcoal)' : 'none'
        }}
      >
        <img src={image.image_thumb || image.image_data || undefined} alt="" loading="lazy" />
      </div>
      <div 
        className="image-card-footer" 
        style={{ padding: '0', position: 'relative' }}
      >
        {/* Reaction display bar — mirrors ClientView style at ~62% scale */}
        <div
          data-testid={`reaction-bar-${image.id}`}
          className="image-reactions image-reactions-mini"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 10px 6px',
            borderBottom: '1px solid var(--kahla-border)',
          }}
        >
          {[
            { key: 'love',    ...getReactionLabel(reactionMode, 'love'),    fallbackIcon: '❤️' },
            { key: 'neutral', ...getReactionLabel(reactionMode, 'neutral'), fallbackIcon: '👍' },
            { key: 'not_me',  ...getReactionLabel(reactionMode, 'not_me'),  fallbackIcon: '✕'  },
          ].map(r => {
            const isActive = image.reaction === r.key;
            // For modes with no icon (simple / decision), show the short
            // label instead so the reaction summary still reads at a glance.
            const display = r.icon || r.shortLabel || r.fallbackIcon;
            return (
              <span
                key={r.key}
                data-testid={`reaction-icon-${r.key}-${image.id}`}
                title={r.label}
                className={`reaction-btn ${r.key.replace('_', '-')} ${isActive ? 'active' : 'inactive'} ${r.icon ? '' : 'is-text'}`}
                style={{
                  cursor: 'default',
                  pointerEvents: 'none',
                  ...(r.icon ? {} : {
                    fontSize: '10px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '2px 6px',
                    border: `1px solid ${isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
                    color: isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                    background: isActive ? 'var(--kahla-cream-light)' : 'transparent',
                  }),
                }}
              >
                {display}
              </span>
            );
          })}
        </div>

        {/* Shared Decision: per-participant reactions */}
        {sharedDecisionMode && image.reactions && Object.keys(image.reactions).length > 0 && (
          <div
            data-testid={`shared-reactions-${image.id}`}
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '14px',
              padding: '4px 10px 6px',
              borderBottom: '1px solid var(--kahla-border)',
            }}
          >
            {[0, 1].map(pid => {
              const r = image.reactions[String(pid)];
              if (!r?.reaction) return null;
              const name = (participants || [])[pid] || `P${pid + 1}`;
              const initial = name.charAt(0).toUpperCase();
              const icon = r.reaction === 'love' ? '❤️' : r.reaction === 'neutral' ? '👍' : '❌';
              return (
                <span key={pid} style={{ fontSize: '11px', color: 'var(--kahla-text-light)', letterSpacing: '0.02em' }}>
                  {initial} {icon}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Label + Final toggle row */}
        <div style={{ padding: '8px 10px', position: 'relative' }}>
        <div 
          ref={dropdownRef}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
        >
          {/* Label chip or Add Label button */}
          <button
            onClick={handleLabelClick}
            onMouseEnter={(e) => { if (image.section_label) e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { if (image.section_label) e.currentTarget.style.opacity = '0.85'; }}
            data-testid={`label-btn-${image.id}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '10px',
              padding: '3px 10px',
              backgroundColor: 'var(--kahla-cream)',
              color: image.section_label ? 'var(--kahla-text)' : 'var(--kahla-taupe)',
              border: image.section_label ? '1px solid var(--kahla-border)' : '1px dashed var(--kahla-border)',
              borderRadius: '0',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease, background-color 0.2s ease',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontWeight: 500,
              opacity: image.section_label ? 0.85 : 1,
              lineHeight: 1,
            }}
          >
            {image.section_label || '+ Label'}
            {image.section_label && (
              <span style={{ fontSize: '7px', opacity: 0.55, marginLeft: '1px' }}>▼</span>
            )}
          </button>
          
          {/* Final choice toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFinal(image.id); }}
            data-testid={`final-toggle-${image.id}`}
            title={image.is_final ? 'Remove from final selections' : 'Mark as final choice'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '2px 4px',
              marginLeft: image.reaction ? '4px' : 'auto',
              opacity: image.is_final ? 1 : 0.35,
              transition: 'opacity 0.2s',
              color: image.is_final ? '#C9A96E' : 'var(--kahla-text-light)',
            }}
          >
            {image.is_final ? '★' : '☆'}
          </button>
          
          {/* Label Dropdown */}
          {showLabelDropdown && (
            <div 
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '10px',
                backgroundColor: 'white',
                border: '1px solid var(--kahla-border)',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                minWidth: '160px',
                maxWidth: '220px',
                maxHeight: '320px',
                zIndex: 100,
                marginBottom: '4px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              data-testid={`label-dropdown-${image.id}`}
              onWheel={(e) => {
                const target = e.currentTarget.querySelector('[data-scroll-container]');
                if (target) {
                  const { scrollTop, scrollHeight, clientHeight } = target;
                  const atTop = scrollTop === 0 && e.deltaY < 0;
                  const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
                  if (!atTop && !atBottom) {
                    e.stopPropagation();
                  }
                }
              }}
              onTouchStart={(e) => {
                e.currentTarget._touchStartY = e.touches[0].clientY;
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Scrollable options container */}
              <div 
                data-scroll-container
                style={{ 
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-y',
                }}
              >
                {/* No label option */}
                <button
                  onClick={() => handleSelectLabel(null)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '11px',
                    textAlign: 'left',
                    border: 'none',
                    borderBottom: '1px solid var(--kahla-border)',
                    backgroundColor: !image.section_label ? 'var(--kahla-cream)' : 'white',
                    cursor: 'pointer',
                    color: 'var(--kahla-text-light)',
                    fontStyle: 'italic',
                  }}
                  data-testid={`label-option-none-${image.id}`}
                >
                  No label
                </button>
                
                {/* Existing labels */}
                {sectionLabels.map(label => (
                  <button
                    key={label}
                    onClick={() => handleSelectLabel(label)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '11px',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: '1px solid var(--kahla-border)',
                      backgroundColor: image.section_label === label ? 'var(--kahla-cream)' : 'white',
                      cursor: 'pointer',
                      color: 'var(--kahla-text)',
                      fontWeight: image.section_label === label ? '600' : '400',
                    }}
                    onMouseEnter={(e) => { if (image.section_label !== label) e.target.style.backgroundColor = 'var(--kahla-cream-light)'; }}
                    onMouseLeave={(e) => { if (image.section_label !== label) e.target.style.backgroundColor = 'white'; }}
                    data-testid={`label-option-${label}-${image.id}`}
                  >
                    {label}
                    {image.section_label === label && ' ✓'}
                  </button>
                ))}
              </div>
              
              {/* Create New Label Input - Fixed at bottom */}
              <div style={{
                padding: '8px',
                borderTop: '1px solid var(--kahla-border)',
                backgroundColor: 'var(--kahla-cream-light)',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={newLabelInput}
                    onChange={(e) => setNewLabelInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    placeholder="New label..."
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      fontSize: '11px',
                      border: '1px solid var(--kahla-border)',
                      outline: 'none',
                      minWidth: 0,
                    }}
                    data-testid={`new-label-input-${image.id}`}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCreateNewLabel(); }}
                    disabled={!newLabelInput.trim()}
                    style={{
                      padding: '5px 10px',
                      fontSize: '10px',
                      backgroundColor: newLabelInput.trim() ? 'var(--kahla-charcoal)' : 'var(--kahla-border)',
                      color: 'white',
                      border: 'none',
                      cursor: newLabelInput.trim() ? 'pointer' : 'not-allowed',
                    }}
                    data-testid={`create-label-btn-${image.id}`}
                  >
                    Add
                  </button>
                </div>
                <p style={{ fontSize: '9px', color: 'var(--kahla-text-light)', marginTop: '4px' }}>
                  Press Enter to create & assign
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Client comment — appears as a quiet quoted line */}
        {allowImageConversations && image.comment && (
          <div
            data-testid={`client-comment-${image.id}`}
            style={{ marginTop: '8px' }}
          >
            <span style={{
              fontSize: '9px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              opacity: 0.75,
            }}>
              {clientName ? `From ${clientName}` : 'From client'}
            </span>
            <p style={{
              fontSize: '11px',
              color: 'var(--kahla-text)',
              marginTop: '3px',
              fontStyle: 'italic',
              lineHeight: 1.45,
              paddingLeft: '8px',
              borderLeft: '2px solid var(--kahla-border)',
              whiteSpace: 'pre-wrap',
            }}>
              {image.comment}
            </p>
          </div>
        )}

        {/* Owner reply — distinct cream block, attached visually to the
            client comment when one exists. Hidden entirely when the
            template plan disables Image Conversations. */}
        {allowImageConversations && (
        <div
          data-testid={`owner-reply-block-${image.id}`}
          style={{
            marginTop: image.comment ? '6px' : '8px',
          }}
        >
          {!showOwnerReply && !image.owner_reply ? (
            <button
              type="button"
              data-testid={`owner-reply-toggle-${image.id}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowOwnerReply(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px 0',
                fontSize: '10px',
                color: 'var(--kahla-taupe)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '0.06em',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(166,152,136,0.35)',
                textUnderlineOffset: '3px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
            >
              {image.comment ? 'Reply on this image' : 'Add a note'}
            </button>
          ) : (
            <div
              style={{
                backgroundColor: 'var(--kahla-cream-light)',
                borderLeft: '2px solid var(--kahla-taupe)',
                padding: '6px 8px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={{
                fontSize: '9px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--kahla-taupe)',
                fontWeight: 500,
              }}>
                {brandName ? `From ${brandName}` : 'From studio'}
              </span>
              <textarea
                data-testid={`owner-reply-input-${image.id}`}
                value={ownerReply}
                onChange={(e) => setOwnerReply(e.target.value.slice(0, 200))}
                onBlur={() => {
                  if ((ownerReply || '') !== (image.owner_reply || '')) {
                    onSaveOwnerReply?.(image.id, ownerReply);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.currentTarget.blur();
                  }
                }}
                placeholder={image.comment ? 'Reply…' : 'Add a quick note…'}
                rows={2}
                maxLength={200}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: '3px',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  lineHeight: 1.45,
                  color: 'var(--kahla-text)',
                  fontStyle: 'italic',
                  padding: 0,
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2px',
              }}>
                <span style={{
                  fontSize: '9px',
                  color: 'var(--kahla-text-light)',
                  opacity: 0.65,
                }}>
                  {ownerReply.length}/200
                </span>
                {image.owner_reply && (
                  <button
                    type="button"
                    data-testid={`owner-reply-clear-${image.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOwnerReply('');
                      onSaveOwnerReply?.(image.id, '');
                      setShowOwnerReply(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontSize: '9px',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'var(--kahla-text-light)',
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        )}
        </div>{/* close label row wrapper */}

        {/* Internal filename row — owner-only, hidden by default. Truncated
            with ellipsis to preserve the card rhythm. Title attribute lets the
            owner read the full name on hover without breaking the layout. */}
        {showFilename && image.filename && (
          <div
            data-testid={`image-filename-${image.id}`}
            title={image.filename}
            style={{
              padding: '4px 12px 8px',
              fontSize: '10px',
              letterSpacing: '0.02em',
              color: 'var(--kahla-text-light)',
              fontFamily: 'Menlo, Consolas, monospace',
              opacity: 0.62,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              borderTop: '1px solid var(--kahla-border)',
              lineHeight: 1.4,
            }}
          >
            {image.filename}
          </div>
        )}
      </div>
    </div>
  );
};

// Tight comparator — re-render this card ONLY when something that
// actually shows in it changes. Identity-stable callbacks from the parent
// are intentionally ignored: the parent owns state and callbacks are
// recreated on every render, so a shallow comparison would defeat the
// memo. Callbacks are read at call time, not compared.
//
// Performance lever: on a 200-image board, any unrelated parent state
// change (toast, hover, modal toggle) re-renders ALL cards without this
// memo. With it, only the cards whose own image doc changed re-render.
function arePropsEqual(prev, next) {
  if (prev.index !== next.index) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.reorderEnabled !== next.reorderEnabled) return false;
  if (prev.allowImageConversations !== next.allowImageConversations) return false;
  if (prev.reactionMode !== next.reactionMode) return false;
  if (prev.brandName !== next.brandName) return false;
  if (prev.clientName !== next.clientName) return false;
  if (prev.sharedDecisionMode !== next.sharedDecisionMode) return false;
  if (prev.participants !== next.participants) return false;
  if (prev.isCover !== next.isCover) return false;
  if (prev.showFilename !== next.showFilename) return false;
  if (prev.sectionLabels !== next.sectionLabels) return false;

  const a = prev.image;
  const b = next.image;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.id !== b.id) return false;
  if (a.image_thumb !== b.image_thumb) return false;
  if (a.image_data !== b.image_data) return false;
  if (a.image_full !== b.image_full) return false;
  if (a.width !== b.width) return false;
  if (a.height !== b.height) return false;
  if (a.section_label !== b.section_label) return false;
  if (a.is_final !== b.is_final) return false;
  if (a.reaction !== b.reaction) return false;
  if (a.comment !== b.comment) return false;
  if (a.owner_reply !== b.owner_reply) return false;
  if (a.filename !== b.filename) return false;
  if (a.shared_decisions !== b.shared_decisions) return false;
  return true;
}

const MemoizedSortableImageCard = React.memo(SortableImageCard, arePropsEqual);

export default MemoizedSortableImageCard;
